<template>
  <div
    :id="`match-${match.id}`"
    :class="match.status"
    :disabled="isLoading"
    class="d-flex align-items-center justify-content-between table-view-match-wrapper"
  >
    <div class="py-4 h-pl-4 h-pr-6 match-index-column">
      <div class="d-flex flex-column justify-content-center mb-5">
        <TextBlock class="match-index" variant="secondary" :size="1"
          >#{{ match.index + 1 }}</TextBlock
        >
      </div>
    </div>
    <div
      class="py-4 h-mr-auto player_name-column"
      :class="{
        'align-self-start': isThirdPlaceMatch(match),
      }"
    >
      <div class="d-flex flex-nowrap">
        <div class="d-flex flex-nowrap player_name align-items-center">
          <PlayerItem
            class="p-0 flex-grow-1 flex-shrink-0"
            :player="match.player_1"
            hide-checkin
            hide-score
            :avatar-size="30"
            variant="transparent"
            :is-profile="isSingle"
            :is-ready-for-battle="!match.is_detained || match.player_1_ready"
            :is-empty="match.player_empty_1"
          />
        </div>
        <div class="h-ml-3 mt-3 flex-shrink-0">
          <ToolIcon
            v-if="match.player_1"
            :help-text="
              name(match.player_1) + ' captain name: ' + match.captain_1
            "
            icon-name="chat"
            :icon-color="$scss.secondaryTextColor"
            icon-size="16"
            variant="default"
            @click.stop.prevent="startOneToOneChat(match.player_1)"
          />
        </div>
      </div>
      <div class="d-flex flex-nowrap align-items-center">
        <div class="mt-3 d-flex flex-nowrap player_name align-items-center">
          <PlayerItem
            class="p-0 flex-grow-1 flex-shrink-0"
            :player="match.player_2"
            hide-checkin
            hide-score
            :avatar-size="30"
            variant="transparent"
            :is-profile="isSingle"
            :is-ready-for-battle="!match.is_detained || match.player_2_ready"
            :is-empty="match.player_empty_2"
          />
        </div>
        <div class="h-ml-3 mt-3">
          <ToolIcon
            v-if="match.player_2"
            :help-text="
              name(match.player_2) + ' captain name: ' + match.captain_2
            "
            icon-name="chat"
            :icon-color="$scss.secondaryTextColor"
            icon-size="16"
            variant="default"
            @click.stop.prevent="startOneToOneChat(match.player_2)"
          />
        </div>
      </div>
      <div class="d-flex align-items-center font-weight-bold mt-3">
        <AppIcon
          name="calendar"
          width="16"
          :fill="$scss.colors.secondaryTextBg"
          class="h-mr-3"
        />
        <TextBlock :size="0.75" :variant="'secondary'" :line-height="1"
          >{{ match.date_start | localDate($times.DATE_SUBMED) }},</TextBlock
        >
        <TextBlock
          class="h-ml-3"
          :size="0.75"
          :variant="'secondary'"
          :line-height="1"
          >{{ match.date_start | localDate($times.TIME_SIMPLE) }}</TextBlock
        >
        <TextBlock :variant="'secondary'" :size="0.75" :line-height="1"
          >&nbsp;&ndash;&nbsp;</TextBlock
        >

        <TextBlock :variant="'secondary'" :size="0.75" :line-height="1">{{
          match.date_end | localDate($times.TIME_SIMPLE)
        }}</TextBlock>
      </div>
      <div>
        <TextLabel v-if="isThirdPlaceMatch(match)">
          {{ $t('web_tournament_third-place-match', 'Match for third Place') }}
        </TextLabel>
      </div>
    </div>
    <div class="py-4 h-ml-5" :style="{ flexBasis: `${46 * numberOfGames}px` }">
      <div v-if="battleInfo" class="mb-5">
        <BattleResultsHorizontal
          :key="`${match.battle_id}-results`"
          :ref="match.battle_id"
          :match="battleInfo"
          :edit-mode="false"
          spacer-class="mt-1 mb-3"
          :is-match-conflicted="isMatchConflicted(match)"
          :is-match-finished="isMatchFinished(match)"
        />
      </div>
    </div>
    <div class="py-4 mx-5 summary-column">
      <div
        v-if="match.status !== BATTLE_STATUSES.cancelled"
        class="mb-5 d-flex flex-column align-items-center"
      >
        <SingleScore :value="getPlayerScore(match, 1)" class="d-inline-flex" />
        <div class="mt-1 mb-3"></div>
        <SingleScore :value="getPlayerScore(match, 2)" />
      </div>
    </div>
    <div class="match-status py-4 h-mr-5">
      <div v-if="isDraw" class="draw-container">
        <p
          class="badge-hera d-inline-flex align-items-center status-label  mb-5"
        >
          {{ $t('_web_tournament_management-draw', 'Draw') }}
        </p>
      </div>
      <StatusLabel
        v-else
        class="w-100 text-center justify-content-center mb-5"
        :status="match.status"
      />
    </div>
    <div class="py-4 h-pr-4 h-pl-4 h-ml-auto actions-column">
      <div
        class="d-flex flex-nowrap justify-content-start align-items-center mb-5"
      >
        <ToolIcon
          v-for="(item, index) in getMatchTools(match)"
          :key="item.key"
          :help-text="item.helpText"
          :icon-name="item.iconName"
          :disabled="item.disabled"
          variant="circle"
          :class="{ 'h-ml-3': index }"
          @click.stop="item.action"
        />
      </div>
    </div>
  </div>
</template>

<script>
//utils
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { getPlayerScore } from '@/views/tournaments/utils/players';
//constants
import { BATTLE_STATUSES } from '@/constants/battles';
//mixins
import matchHelper from '@/views/tournaments/management/brackets/mixins/matchHelper';
import loadingHelper from '@/mixins/isLoading';
//components
import AppIcon from '@/components/atoms/Icon';
import PlayerItem from '@/components/tournaments/playerItem.vue';
import TextBlock from '@/components/atoms/TextBlock';
import StatusLabel from '@/components/molecules/StatusLabel';
import BattleResultsHorizontal from '@/components/tournaments/ResultsHorizontal.vue';
import ToolIcon from '@/views/tournaments/management/brackets/components/ToolIcon';
import TextLabel from '@/components/atoms/TextLabel';
import SingleScore from '@/components/tournaments/SingleScore';
import { prop } from '@/utils/factories';
import { CLAN } from '@/constants/accounts';

export default {
  name: 'TournamentTableViewRowItem',
  components: {
    AppIcon,
    PlayerItem,
    TextBlock,
    StatusLabel,
    BattleResultsHorizontal,
    TextLabel,
    SingleScore,
    ToolIcon,
  },
  mixins: [loadingHelper, matchHelper],
  props: {
    match: prop(Object),
    roundName: prop(String),
    numberOfGames: prop(Number, 1),
    prevRoundEndDate: prop(Number),
  },
  data() {
    return {
      getPlayerScore,
      BATTLE_STATUSES,
      battleInfo: null,
    };
  },
  computed: {
    ...mapGetters(['isSingle', 'currentTournamentBlock']),
    tournamentID() {
      return this.$route.params.tournamentID;
    },
    isDraw() {
      return (
        this.match.player_1_result === 'tie' &&
        this.match.player_2_result === 'tie'
      );
    },
  },

  created() {
    this.getResults();
  },
  mounted() {},
  methods: {
    ...mapMutations(['openConversationInSliderChatEditor']),
    ...mapActions([
      'getBattleResults',
      'errorNotify',
      'successNotify',
      //chats
      'createTournamentConversationBySignature',
      'openModal',
      'startTournamentChat',
    ]),
    getResults() {
      if (this.match.battle_id) {
        this.setLoading();
        this.getBattleResults({
          rounds: this.match.number_of_rounds,
          battleID: this.match.battle_id,
        })
          .then(response => {
            this.battleInfo = {
              ...response.match_results,
              battle_id: this.match.battle_id,
              number_of_rounds: this.match.number_of_rounds,
            };
          })
          .catch(this.errorNotify)
          .finally(this.unsetLoading);
      }
    },
    getMatchTools(match) {
      return [
        {
          helpText: this.$t(
            '_web_battles_schedule-match-help',
            'Change schedule of the match'
          ),
          iconName: 'calendar',
          disabled:
            !this.canChangeMatchDate(match.status) &&
            !this.canChangeMatchDuration(match.status),
          action: () => this.openMatchSchedulingModal(match.index),
        },
        {
          helpText: this.$t('_web_battles-group-chat', 'Group chat'),
          iconName: 'chat-group',
          disabled: false,
          action: () => this.openMatchChat(match),
        },
        {
          helpText: this.$t(
            '_web_tournament_match-menu-item-edit-results',
            'Edit results'
          ),
          iconName: 'scales',
          disabled: !this.canViewMatchResult(match),
          action: () => this.openMatchResultsEdit(match),
        },
        {
          helpText: this.$t('_web_battles-reset-match', 'Reset match'),
          iconName: 'reset',
          disabled: !this.canRestartMatch(match),
          action: () => this.confirmResetMatch(match),
        },
        {
          helpText: this.$t('_web_battles-start-match', 'Start match'),
          iconName: 'play-2',
          disabled: !this.canStartMatch(match),
          action: () => this.confirmStartMatch(match),
        },
        {
          helpText: this.$t('_web_battles-finish-match', 'Finish match'),
          iconName: 'stop',
          disabled: !this.canFinishMatch(match),
          action: () => this.confirmFinishMatch(match),
        },
      ];
    },
    name(player) {
      return player ? this.$filters.displayName(player) : '';
    },
    openMatchChat(match) {
      this.openChat({
        ...match,
        conversationType: 'group_chat',
        targetGroup: 'all',
      });
    },
    openMatchSchedulingModal() {
      this.openModal({
        name: 'MatchSchedulingModal',
        props: {
          match: this.match,
          minDate: this.prevRoundEndDate,
          canChangeMatchDate: this.canChangeMatchDate(this.match.status),
          canChangeMatchDuration: this.canChangeMatchDuration(
            this.match.status
          ),
        },
        events: {
          save: data =>
            this.$emit('matchUpdated', {
              ...this.match,
              ...data,
            }),
        },
        settings: {
          clickOnBackdrop: false,
          variant: 'fit-content',
        },
      });
    },
    openChat({
      conversationType,
      targetGroup,
      roundIndex,
      id,
      index: matchIndex,
    }) {
      const options = {
        tournamentId: this.tournamentID,
        round: roundIndex,
        conversationType,
        targetGroup,
        captainsOnly: true,
        visible: false,
        blockId: this.currentTournamentBlock.id,
      };
      if (targetGroup === 'all' && id) {
        options.matchId = id;
      }
      this.createTournamentConversationBySignature(options)
        .then(conversation =>
          this.openConversationInSliderChatEditor({
            conversation,
            predefinedTitle:
              options.matchId &&
              this.$t('_web_match_chat_title', `Match Id: {match} `, {
                match: matchIndex + 1,
              }),
            predefinedSubtitle: '',
          })
        )
        .catch(this.errorNotify);
    },
    /**
     * Results modal help funcs
     */
    openMatchResultsEdit(obj) {
      this.$router.push({
        name: 'BlockBracketsTableEditResults',
        params: {
          ...this.$route.params,
          matchIndex: obj.index,
          match: {
            ...obj,
            battleInfo: this.battleInfo,
          },
          roundName: this.roundName,
        },
        query: {
          ...this.$route.query,
          scrollOffset: window.pageYOffset,
        },
      });
    },
    startOneToOneChat({ id, login, type }) {
      this.startTournamentChat({ id: type === CLAN ? id : login }).catch(
        this.errorNotify
      );
    },
    /**
     * Match actions functions
     */
    onMatchStart(match) {
      return this.startMatch(match).then(() => {
        this.$emit('matchStarted', match.index);
      });
    },
    onMatchFinish(match) {
      return this.finishMatch(match).then(() => {
        this.$emit('matchFinished', match.index);
      });
    },
    onMatchReset(match) {
      return this.resetMatch(match).then(() => {
        this.$emit('matchReseted', match.index);
      });
    },
    /**
     * Match confirm functions
     */
    confirmResetMatch(match) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_restart-confirm-message',
            'Now this match will be reset. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onMatchReset.bind(null, match),
        },
      });
    },
    confirmStartMatch(match) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_start-confirm-message',
            'Now this match will be started. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onMatchStart.bind(null, match),
        },
      });
    },
    confirmFinishMatch(match) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_finish-confirm-message',
            'Now this match will be finished. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onMatchFinish.bind(null, match),
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.table-view-match-wrapper {
  flex-basis: fit-content;
  .player_name-column {
    flex-basis: 184px;
    .player_name {
      position: relative;
      max-width: 160px;
      width: 100%;
    }
  }
  .summary-column {
    flex-basis: 85px;
  }
  &.striped {
    background: rgba($ph-popover-bg-raw, 0.2);
  }
  & > div {
    &.match-index-column {
      .match-index {
        font-size: 1rem !important;
      }
    }
    &.match-status {
      flex-basis: 130px;
      max-width: 130px;
      span {
        max-width: 80px;
      }
    }
    .loader-wrapper {
      height: 200px;
    }
  }

  .draw-container {
    @include auto-rtl(margin-left, 6px);
  }
}
</style>
